import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import PageShell from "@/components/PageShell";
import NextRoom from "@/components/NextRoom";
import RelatedGraph from "@/components/RelatedGraph";
import { BUILDING, getPillar, artifactsForBuilding } from "@/data/content";

const CATEGORY_CONTEXT = {
  "Ventures": {
    what: "A venture is a company Quentin owns or operates. Entries in this category are ongoing commitments, not one-off projects.",
    standard: "A venture earns a full public case study when the results are settled, the lessons are clear, and the story would be honest without editing. Until then, the entry states what it is and links to the thinking that informs it.",
  },
  "Professional Work": {
    what: "Professional Work covers engagements where Quentin brought experience to another organization: operations, marketing, strategy, brand, or leadership contribution.",
    standard: "Professional entries are stated conservatively. Names, results, and specifics appear only when the engagement is complete and both sides are aligned on what can be said.",
  },
  "Creative Work": {
    what: "Creative Work is where Quentin has been the creative mind behind a brand and its website: identity, positioning, voice, and the surfaces where a business is first understood.",
    standard: "The strongest evidence of creative work is the live site itself. The link is provided so the work speaks first. Case studies follow only when they add something the site does not.",
  },
  "Collaborations": {
    what: "Collaborations are relationships where two or more people build together, credit is shared, and the outcome would not exist without the pairing.",
    standard: "These entries stay understated on purpose. Details appear when both parties are ready, not when one is.",
  },
  "Strategic Interests": {
    what: "A strategic interest is a possibility Quentin is tracking or preparing for: a market, a role, or a form of ownership that is not yet a commitment.",
    standard: "Naming an interest publicly is a discipline, not an announcement. It clarifies focus without overpromising the future.",
  },
};

export default function BuildingDetail() {
  const { slug } = useParams();
  const entry = BUILDING.find((b) => b.slug === slug);
  if (!entry) return <Navigate to="/building" replace />;

  const siblings = BUILDING.filter((b) => b.category === entry.category && b.slug !== entry.slug).slice(0, 4);
  const idx = BUILDING.findIndex((b) => b.slug === entry.slug);
  const prev = BUILDING[(idx - 1 + BUILDING.length) % BUILDING.length];
  const next = BUILDING[(idx + 1) % BUILDING.length];

  return (
    <PageShell
      testId="page-building-detail"
      title={`${entry.name} | Building | Quentin Young`}
      description={`${entry.lead} A ${entry.category.toLowerCase()} entry in Quentin Young's building archive.`}
      ogImage="/og-building.jpg"
      breadcrumbs={[
        { name: "Home", url: "/" },
        { name: "Building", url: "/building" },
        { name: entry.name, url: `/building/${entry.slug}` },
      ]}
    >
      <article className="container-edge pt-16 md:pt-24 pb-16">
        <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-paper-mute mb-6">
          <Link to="/building" className="link-quiet">Building</Link> · {entry.category} · {entry.status}
        </p>
        <h1 className="display-serif text-4xl md:text-6xl lg:text-7xl text-paper-primary text-balance leading-[1.02]">
          {entry.name}
        </h1>
        <p className="mt-8 max-w-2xl font-serif text-xl md:text-2xl text-paper-secondary leading-relaxed text-pretty">
          {entry.lead}
        </p>
        {entry.url && (
          <a
            href={entry.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="building-detail-extlink"
            className="mt-8 inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.22em] text-gold-warm hover:text-gold transition-colors duration-300 border-b border-gold/30 hover:border-gold pb-1"
            aria-label={`Visit ${entry.name} website (opens in new tab)`}
          >
            <span>Visit the site: {new URL(entry.url).host.replace(/^www\./, "")}</span>
            <span>↗</span>
          </a>
        )}
      </article>

      <section className="container-edge pb-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 border-t border-ink-line pt-10">
          <div className="md:col-span-3">
            <p className="eyebrow">Status</p>
            <p className="mt-2 font-mono text-sm text-paper-primary">{entry.status}</p>
            <p className="eyebrow mt-8">Category</p>
            <p className="mt-2 font-mono text-sm text-paper-primary">{entry.category}</p>
            {entry.role && (
              <>
                <p className="eyebrow mt-8">Role</p>
                <p className="mt-2 font-mono text-sm text-paper-primary">{entry.role}</p>
              </>
            )}
            {entry.url && (
              <>
                <p className="eyebrow mt-8">Site</p>
                <a href={entry.url} target="_blank" rel="noopener noreferrer" className="mt-2 block font-mono text-sm text-gold-warm hover:text-gold break-all">
                  {new URL(entry.url).host.replace(/^www\./, "")}
                </a>
              </>
            )}
          </div>
          <div className="md:col-span-9 prose-quentin">
            <h2 className="eyebrow-gold mb-4">Entry</h2>
            <div className="space-y-5 text-paper-secondary text-lg leading-relaxed">
              <p>{entry.body}</p>
              {CATEGORY_CONTEXT[entry.category] && (
                <div className="border-l border-gold/30 pl-6 my-2" data-testid="building-category-context">
                  <p className="eyebrow mb-3">What {entry.category} means here</p>
                  <p className="text-paper-secondary text-base leading-relaxed text-pretty">{CATEGORY_CONTEXT[entry.category].what}</p>
                  <p className="mt-3 text-paper-tertiary text-base leading-relaxed text-pretty">{CATEGORY_CONTEXT[entry.category].standard}</p>
                </div>
              )}
              {entry.pillar && getPillar(entry.pillar) && (
                <div data-testid="building-pillar-lens">
                  <p className="eyebrow mb-3">Under the lens of {getPillar(entry.pillar).name}</p>
                  <p className="text-paper-secondary text-base leading-relaxed text-pretty">
                    {getPillar(entry.pillar).definition || getPillar(entry.pillar).body || `This entry sits under the ${getPillar(entry.pillar).name} pillar of the philosophy.`}
                  </p>
                  <Link
                    to={`/philosophy#pillar-${entry.pillar}`}
                    className="mt-4 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-gold/70 hover:text-gold-warm transition-colors duration-300"
                    data-testid="building-pillar-link"
                  >
                    <span>Read the pillar</span>
                    <span>→</span>
                  </Link>
                </div>
              )}
              <p className="text-paper-tertiary italic font-serif">
                Every Building entry will, in time, answer six questions: What was the context? What was observed?
                What was built, shaped, improved, or contributed? What changed? What was learned? Which ideas does this connect to?
              </p>
              <p className="text-paper-tertiary text-sm">
                Until the work earns a complete answer to each, the public entry remains intentionally restrained.
                Silence is preferable to filler.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Siblings */}
      {siblings.length > 0 && (
        <section className="border-t border-ink-line bg-ink-surface py-16">
          <div className="container-edge">
            <p className="eyebrow mb-8">Also in {entry.category}</p>
            <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-ink-line">
              {siblings.map((s) => (
                <li key={s.slug} className="bg-ink-surface">
                  <Link to={`/building/${s.slug}`} className="block p-6 group" data-testid={`building-sibling-${s.slug}`}>
                    <p className="eyebrow mb-2">{s.status}</p>
                    <h3 className="font-serif text-xl text-paper-primary group-hover:text-gold-warm transition-colors">{s.name}</h3>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <section className="border-t border-ink-line bg-ink-base">
        <div className="container-edge grid grid-cols-2 gap-px bg-ink-line">
          <Link to={`/building/${prev.slug}`} className="bg-ink-base p-8 md:p-10 group" data-testid="building-prev">
            <p className="eyebrow mb-3">← Previous venture</p>
            <p className="font-serif text-xl text-paper-primary group-hover:text-gold-warm transition-colors">{prev.name}</p>
          </Link>
          <Link to={`/building/${next.slug}`} className="bg-ink-base p-8 md:p-10 group text-right" data-testid="building-next">
            <p className="eyebrow mb-3">Explore another venture →</p>
            <p className="font-serif text-xl text-paper-primary group-hover:text-gold-warm transition-colors">{next.name}</p>
          </Link>
        </div>
      </section>

      <RelatedGraph
        observations={(entry.relatedObservations || []).slice(0, 4)}
        artifacts={artifactsForBuilding(entry.slug).map(a => a.slug)}
        pillar={entry.pillar || null}
        eyebrow="Follow the thread"
        title="What this connects to."
      />

      <NextRoom
        eyebrow="Move to the next room"
        title="Proof is only useful when it is connected."
        rooms={[
          (entry.relatedObservations && entry.relatedObservations[0])
            ? { label: "Read the thinking behind this work", to: `/thinking/${entry.relatedObservations[0]}`, hint: "thinking" }
            : { label: "Read the thinking behind the work", to: "/thinking", hint: "thinking" },
          entry.pillar
            ? { label: `Under the lens of ${getPillar(entry.pillar).name}`, to: `/philosophy#pillar-${entry.pillar}`, hint: "framework" }
            : { label: "Study the framework", to: "/philosophy", hint: "framework" },
          { label: "Continue through the building slate", to: `/building/${next.slug}`, hint: "another venture" },
        ]}
        testId="building-detail-next"
      />
    </PageShell>
  );
}
